import { differenceInCalendarMonths, parse } from 'date-fns'

import { LicenceType } from 'types'
import { CompleteDateObject } from 'src/types/date'
import { convertDateObjectIntoDate } from 'utils'

const getErrorMessage = (minimumLicenceAge: number, isAdditionalDriver?: boolean) => {
  return `The date ${isAdditionalDriver ? 'they' : 'you'} passed ${isAdditionalDriver ? 'their' : 'your'} test must be at least ${minimumLicenceAge} years after ${isAdditionalDriver ? 'their' : 'your'} date of birth.`
}

const internationalLicenceTypes = [
  LicenceType.EUROPEAN,
  LicenceType.INTERNATIONAL,
  LicenceType.OTHER_EUROPEAN,
]

const getMinimumLicenceAgeByLicenceType = (
  licenceType: LicenceType,
  isFirstInternationalLicence?: boolean,
) => {
  if (internationalLicenceTypes.includes(licenceType) || isFirstInternationalLicence) {
    return 14
  }

  if (licenceType === LicenceType.UK_PROVISIONAL) {
    return 16
  }

  return 17
}

type Args = {
  dateOfBirth: CompleteDateObject
  licenceType: LicenceType
  dateLicenceReceived: { year: string; month: string }
  isFirstInternationalLicence?: boolean
  isAdditionalDriver?: boolean
}

export const licenceAgeValidator = ({
  dateOfBirth,
  licenceType,
  dateLicenceReceived,
  isFirstInternationalLicence,
  isAdditionalDriver,
}: Args) => {
  if (
    !dateOfBirth.day ||
    !dateOfBirth.month ||
    !dateOfBirth.year ||
    !licenceType ||
    !dateLicenceReceived.month ||
    !dateLicenceReceived.year
  ) {
    return { isSuccess: false }
  }

  const formattedDateOfBirth = convertDateObjectIntoDate(dateOfBirth)

  const formattedDateLicenceReceived = parse(
    `${dateLicenceReceived.year}-${dateLicenceReceived.month}`,
    'yyyy-MMMM',
    new Date(),
  )

  // We use differenceInCalendarMonths to inclusively calculate the difference in months between the two dates
  // This means that the difference between a DOB of 31/01/2000 and a licence date of 01/2017 would return 17 years
  // This is necessary because we don't have the exact day of the month for the licence received date
  const ageLicenceObtained =
    differenceInCalendarMonths(formattedDateLicenceReceived, formattedDateOfBirth) / 12

  const minimumLicenceAge = getMinimumLicenceAgeByLicenceType(
    licenceType,
    isFirstInternationalLicence,
  )

  if (ageLicenceObtained < minimumLicenceAge) {
    return getErrorMessage(minimumLicenceAge, isAdditionalDriver)
  }

  return { isSuccess: true }
}
